import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  createProjectDocumentRevision,
  deleteProjectDocumentRevision,
  getProjectDocumentRevision,
  getProjectDocumentRevisionApproval,
  getProjectDocumentRevisions,
  updateProjectDocumentRevision,
  updateRevisionNoteAndLink
} from '../../api/project-document-revisions';
import { Purpose } from '../MasterData/Purpose/purposeSlice';
import { Revision } from '../MasterData/Revision/revisionSlice';
import { DocumentRevisionParam } from '../ProjectDocument/DocumentView';
import { getProjectDocumentAsync } from '../ProjectDocument/projectDocumentSlice';
import { ProjectDocumentRevisionFile } from '../ProjectDocumentRevisionFile/projectDocumentRevisionFileSlice';
import { DocumentWorkflowWithStepAndApproval } from '../ProjectDocumentWorkflow/projectDocumentWorkflowSlice';

export const getProjectDocumentRevisionsAsync = createAsyncThunk(
  'projectDocumentRevisions/getProjectDocumentRevisions',
  getProjectDocumentRevisions
);

export const getProjectDocumentRevisionAsync = createAsyncThunk(
  'projectDocumentRevisions/getProjectDocumentRevision',
  async (projectDocumentRevisionId: number, thunkAPI) => {
    const projectDocumentRevision = await getProjectDocumentRevision(
      projectDocumentRevisionId
    );
    thunkAPI.dispatch(
      getProjectDocumentAsync(projectDocumentRevision.projectDocumentId)
    );
    return projectDocumentRevision;
  }
);

export const getProjectDocumentRevisionApprovalAsync = createAsyncThunk(
  'projectDocumentRevisions/getProjectDocumentRevisionApproval',
  async (projectDocumentRevisionId: number, thunkAPI) => {
    const projectDocumentRevision = await getProjectDocumentRevisionApproval(
      projectDocumentRevisionId
    );
    thunkAPI.dispatch(
      getProjectDocumentAsync(projectDocumentRevision.projectDocumentId)
    );
    return projectDocumentRevision;
  }
);

export const createProjectDocumentRevisionAsync = createAsyncThunk(
  'projectDocumentRevisions/createProjectDocumentRevision',
  createProjectDocumentRevision
);

export const updateProjectDocumentRevisionAsync = createAsyncThunk(
  'projectDocumentRevisions/updateProjectDocumentRevision',
  updateProjectDocumentRevision
);

export const updateRevisionNoteAndLinkAsync = createAsyncThunk(
  'projectDocumentRevisions/updateRevisionNoteAndLink',
  updateRevisionNoteAndLink
);

export const deleteProjectDocumentRevisionAsync = createAsyncThunk(
  'projectDocumentRevisions/deleteProjectDocumentRevision',
  async (
    {
      projectDocumentRevisionId,
      docRevParams
    }: {
      projectDocumentRevisionId: number;
      docRevParams: DocumentRevisionParam;
    },
    thunkAPI
  ) => {
    await deleteProjectDocumentRevision(projectDocumentRevisionId);
    thunkAPI.dispatch(getProjectDocumentRevisionsAsync(docRevParams));
  }
);

export interface ProjectDocumentRevision {
  id: number;
  purposeId: number;
  purpose: Purpose;
  revisionId: number;
  revision: Revision;
  manHour: number;
  planIssueDate: Date;
  planCompletedDate: Date;
  projectDocumentId: number;
  currentProjectDocumentWorkflowId: number;
  projectDocumentRevisionFiles: ProjectDocumentRevisionFile[];
  note: string | null;
  linkName: string | null;
  linkUrl: string | null;
  isAutoApproval: boolean | null;
}

export interface ProjectDocumentRevisionWithCurrentWorkflow
  extends ProjectDocumentRevision {
  projectDocumentWorkflows: DocumentWorkflowWithStepAndApproval[];
}

interface ProjectDocumentRevisionState {
  data: ProjectDocumentRevisionWithCurrentWorkflow[];
  count: number;
  selectedProjectDocumentRevision: ProjectDocumentRevisionWithCurrentWorkflow | null;
}

const initialState: ProjectDocumentRevisionState = {
  data: [],
  count: 0,
  selectedProjectDocumentRevision: null
};

export default createSlice({
  name: 'projectDocumentRevisions',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      getProjectDocumentRevisionsAsync.fulfilled,
      (state, action) => {
        state.data = action.payload.data;
        state.count = action.payload.count;
      }
    );
    builder.addCase(
      getProjectDocumentRevisionAsync.fulfilled,
      (state, action) => {
        state.selectedProjectDocumentRevision = action.payload;
      }
    );
    builder.addCase(
      getProjectDocumentRevisionApprovalAsync.fulfilled,
      (state, action) => {
        state.selectedProjectDocumentRevision = action.payload;
      }
    );
  }
});
